<template>
  <CenteredModal v-model="dialog" :show-close-button="true" max-width="460px">
    <div class="bug-report">
      <!-- Icon -->
      <div class="bug-report__icon">
        <v-icon size="44" color="brand">mdi-bug-outline</v-icon>
      </div>

      <!-- Title -->
      <h3 class="bug-report__title">{{ t('bugReport') }}</h3>
      <p class="bug-report__subtitle">{{ t('bugDescriptionPlaceholder') }}</p>

      <!-- Description -->
      <div class="bug-report__field">
        <label class="bug-report__label">{{ t('bugDescription') }}</label>
        <textarea
          v-model="message"
          class="bug-report__textarea"
          :placeholder="t('bugDescriptionPlaceholder')"
          rows="4"
          maxlength="2000"
        />
      </div>

      <!-- Attachments -->
      <div class="bug-report__field">
        <label class="bug-report__label">
          {{ t('attachImages') }}
          <span class="bug-report__hint">(max {{ maxImages }} · {{ maxSizeMB }} MB)</span>
        </label>

        <button
          v-if="selectedFiles.length < maxImages"
          type="button"
          class="bug-report__upload"
          @click="fileInputRef?.click()"
        >
          <v-icon size="22" class="mr-2">mdi-image-plus</v-icon>
          <span>{{ t('selectImages') }}</span>
        </button>

        <input
          ref="fileInputRef"
          type="file"
          accept="image/*"
          multiple
          hidden
          @change="onFileInput"
        />

        <!-- Previews -->
        <div v-if="imagePreviews.length > 0" class="bug-report__previews">
          <div v-for="(preview, index) in imagePreviews" :key="index" class="bug-report__preview">
            <img :src="preview" :alt="`bug-image-${index}`" />
            <button type="button" class="bug-report__preview-remove" @click="removeImage(index)">
              <v-icon size="14" color="white">mdi-close</v-icon>
            </button>
          </div>
        </div>
      </div>

      <!-- Messages -->
      <div v-if="errorMessage" class="bug-report__alert bug-report__alert--error">
        <v-icon size="18" class="mr-2">mdi-alert-circle-outline</v-icon>
        <span>{{ errorMessage }}</span>
      </div>
      <div v-if="successMessage" class="bug-report__alert bug-report__alert--success">
        <v-icon size="18" class="mr-2">mdi-check-circle-outline</v-icon>
        <span>{{ successMessage }}</span>
      </div>

      <!-- Actions -->
      <div class="bug-report__actions">
        <button class="bug-report__btn bug-report__btn--cancel" type="button" :disabled="loading" @click="closeDialog">
          {{ t('cancel') }}
        </button>
        <button
          class="bug-report__btn bug-report__btn--submit"
          type="button"
          :disabled="!message.trim() || loading || !!successMessage"
          @click="handleSend"
        >
          <v-progress-circular v-if="loading" size="18" width="2" indeterminate color="white" class="mr-2" />
          <v-icon v-else size="18" class="mr-2">mdi-send</v-icon>
          <span>{{ t('send') }}</span>
        </button>
      </div>
    </div>
  </CenteredModal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import axiosInstance from '@/services/axiosInstance';
import { BUILD_VERSION } from '@/version';
import CenteredModal from '@/components/CenteredModal.vue';

const props = defineProps<{
  show: boolean;
}>();

const emit = defineEmits(['update:show']);

const { t } = useI18n({ useScope: 'global' });

const message = ref('');
const selectedFiles = ref<File[]>([]);
const imagePreviews = ref<string[]>([]);
const loading = ref(false);
const errorMessage = ref('');
const successMessage = ref('');
const fileInputRef = ref<HTMLInputElement | null>(null);

const maxImages = 5;
const maxSizeMB = 5;
const maxSizeBytes = maxSizeMB * 1024 * 1024;

const dialog = computed({
  get: () => props.show,
  set: (val) => emit('update:show', val),
});

const onFileInput = (e: Event) => {
  const input = e.target as HTMLInputElement;
  if (!input.files) return;
  addFiles(Array.from(input.files));
  input.value = '';
};

const addFiles = (files: File[]) => {
  errorMessage.value = '';
  for (const file of files) {
    if (selectedFiles.value.length >= maxImages) {
      errorMessage.value = t('maxImagesError', { count: maxImages });
      break;
    }
    if (!file.type.startsWith('image/')) {
      errorMessage.value = t('notImageError', { name: file.name });
      continue;
    }
    if (file.size > maxSizeBytes) {
      errorMessage.value = t('fileTooLargeError', { name: file.name, size: maxSizeMB });
      continue;
    }
    selectedFiles.value.push(file);
    const reader = new FileReader();
    reader.onload = (ev) => {
      if (ev.target?.result) imagePreviews.value.push(ev.target.result as string);
    };
    reader.readAsDataURL(file);
  }
};

const removeImage = (index: number) => {
  selectedFiles.value.splice(index, 1);
  imagePreviews.value.splice(index, 1);
};

watch(() => props.show, (val) => {
  if (val) {
    message.value = '';
    selectedFiles.value = [];
    imagePreviews.value = [];
    errorMessage.value = '';
    successMessage.value = '';
  }
});

const closeDialog = () => {
  dialog.value = false;
};

const handleSend = async () => {
  if (!message.value.trim()) return;

  loading.value = true;
  errorMessage.value = '';
  successMessage.value = '';

  try {
    const formData = new FormData();
    formData.append('content', message.value);
    formData.append('type', 'BUG_REPORT');
    formData.append('subject', 'Bug Report');
    formData.append('version', String(BUILD_VERSION));

    for (const file of selectedFiles.value) {
      formData.append('images', file);
    }

    await axiosInstance.post('/messages/bug-report', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    successMessage.value = t('bugReportSent');

    setTimeout(() => {
      closeDialog();
    }, 1800);
  } catch (err: any) {
    console.error('Error sending bug report:', err);
    errorMessage.value = err.response?.data?.message || t('bugReportError');
  } finally {
    loading.value = false;
  }
};
</script>

<style lang="scss" scoped>
.bug-report {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;

  &__icon {
    margin-bottom: 4px;
  }

  &__title {
    font-size: 20px;
    font-weight: 700;
    color: rgb(var(--v-theme-sheetText));
    margin: 0;
  }

  &__subtitle {
    font-size: 13px;
    color: rgba(var(--v-theme-sheetText), 0.6);
    text-align: center;
    margin: 0 0 4px;
    line-height: 1.4;
  }

  &__field {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__label {
    font-size: 13px;
    font-weight: 600;
    color: rgba(var(--v-theme-sheetText), 0.85);
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__hint {
    font-size: 11px;
    font-weight: 400;
    color: rgba(var(--v-theme-sheetText), 0.5);
  }

  &__textarea {
    width: 100%;
    border-radius: 12px;
    border: 1px solid rgba(var(--v-theme-sheetText), 0.15);
    background: rgba(var(--v-theme-sheetText), 0.04);
    color: rgb(var(--v-theme-sheetText));
    padding: 12px 14px;
    font-size: 14px;
    font-family: inherit;
    resize: vertical;
    outline: none;
    transition: border-color 0.15s ease, background 0.15s ease;

    &::placeholder {
      color: rgba(var(--v-theme-sheetText), 0.4);
    }

    &:focus {
      border-color: rgba(var(--v-theme-primary), 0.6);
      background: rgba(var(--v-theme-sheetText), 0.06);
    }
  }

  &__upload {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 12px 16px;
    border-radius: 12px;
    border: 1px dashed rgba(var(--v-theme-sheetText), 0.25);
    background: rgba(var(--v-theme-sheetText), 0.04);
    color: rgb(var(--v-theme-sheetText));
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      background: rgba(var(--v-theme-sheetText), 0.08);
      border-color: rgba(var(--v-theme-sheetText), 0.4);
    }
  }

  &__previews {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 4px;
  }

  &__preview {
    position: relative;
    width: 72px;
    height: 72px;
    border-radius: 10px;
    overflow: hidden;
    background: rgba(var(--v-theme-sheetText), 0.06);

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__preview-remove {
    position: absolute;
    top: 4px;
    right: 4px;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.6);
    border: none;
    cursor: pointer;

    &:hover {
      background: rgba(244, 67, 54, 0.85);
    }
  }

  &__alert {
    width: 100%;
    display: flex;
    align-items: flex-start;
    padding: 10px 12px;
    border-radius: 10px;
    font-size: 13px;
    line-height: 1.4;

    &--error {
      background: rgba(244, 67, 54, 0.1);
      border: 1px solid rgba(244, 67, 54, 0.25);
      color: #ff8a80;
    }

    &--success {
      background: rgba(76, 175, 80, 0.12);
      border: 1px solid rgba(76, 175, 80, 0.3);
      color: #a5d6a7;
    }
  }

  &__actions {
    display: flex;
    gap: 12px;
    width: 100%;
    margin-top: 4px;
  }

  &__btn {
    flex: 1;
    padding: 12px 16px;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &--cancel {
      background: rgba(var(--v-theme-sheetText), 0.08);
      color: rgb(var(--v-theme-sheetText));

      &:hover:not(:disabled) {
        background: rgba(var(--v-theme-sheetText), 0.14);
      }
    }

    &--submit {
      background: rgb(var(--v-theme-primary));
      color: #fff;

      &:hover:not(:disabled) {
        filter: brightness(1.08);
      }

      &:active:not(:disabled) {
        transform: scale(0.97);
      }
    }
  }
}
</style>

<style lang="scss">
/* Override CenteredModal padding only for the bug-report dialog */
.centered-modal:has(> .modal-content > .bug-report) {
  padding: 22px 20px !important;
}
</style>
